import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { createPortal } from "react-dom";

import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

import api from "@/services/api";
import TopMenu from "./components/TopMenu";
import MultiSelect from "@/components/MultiSelect";
import ItemModal from "../components/itemModal";

export default ({ project }) => {
  const [items, setItems] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    search: "",
    statuses: ["TODO", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO"],
    urgent: [],
    module_ids: [],
    roadlog_tags: [],
    is_in_backlog: "",
  });
  const [sortBy, setSortBy] = useState({ key: "points", value: 1 }); // Sort by points desc by default
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const itemModalId = query.get("item_modal_id");

  useEffect(() => {
    fetchItems();
  }, [filters]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/roadlog_item/search", { project_id: project._id, exclude_categories: ["BUG"], ...filters });
      if (!ok) return toast.error("Failed to fetch roadlog items");

      // Fetch budgets
      const { data: budgetsData } = await api.post("/budget/search", { projectId: project._id, status: "active" });
      setBudgets(budgetsData);

      setItems(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch roadlog items");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (itemId, field, value) => {
    try {
      const updateData = typeof field === "object" ? field : { [field]: value };
      const { ok, data } = await api.put(`/roadlog_item/${itemId}`, updateData);
      if (!ok) return toast.error("Failed to update");

      const index = items.findIndex((item) => item._id === itemId);
      const newItems = [...items];
      newItems[index] = { ...newItems[index], ...updateData };
      setItems(newItems);
      toast.success(`Updated successfully`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update");
    }
  };

  const calculateEstimation = (item) => {
    // Only apply dynamic calculation for Stipulio project
    if (project._id !== "667be3511b6919070dfa1ae5") {
      return Number(item.estimation || 0);
    }

    if (!item.days_required) return 0;
    const assignedUserId = item.users && item.users.length > 0 ? item.users[0].user_id : null;

    if (!assignedUserId) {
      return 500 * item.days_required;
    }

    const userTjmOverride = (project.user_tjms_override || []).find((override) => override.userId === assignedUserId);

    if (!userTjmOverride) {
      return 500 * item.days_required;
    }

    return userTjmOverride.tjms * item.days_required;
  };

  const sortedItems = items.sort((a, b) => {
    if (sortBy.key === "created_at") return sortBy.value * (new Date(b.created_at) - new Date(a.created_at));
    if (sortBy.key === "points") return sortBy.value * (b.points - a.points);
    if (sortBy.key === "index") return sortBy.value * (a.index - b.index);
    if (["estimation", "days_required"].includes(sortBy.key)) {
      return sortBy.value * ((a[sortBy.key] || 0) - (b[sortBy.key] || 0));
    }
    if (sortBy.key === "urgent") {
      const aValue = a[sortBy.key] ? 1 : 0;
      const bValue = b[sortBy.key] ? 1 : 0;
      return sortBy.value * (aValue - bValue);
    }
    return sortBy.value * (a[sortBy.key] || "").localeCompare(b[sortBy.key] || "");
  });

  const itemsWithCumulativeCost = sortedItems.reduce((acc, item, index) => {
    const previousCumulativeCost = index === 0 ? 0 : acc[index - 1].cumulativeCost;
    const currentCost = calculateEstimation(item);
    // Arrondir à 2 décimales pour éviter les erreurs de précision flottante
    const cumulativeCost = Math.round((previousCumulativeCost + currentCost) * 100) / 100;

    acc.push({ ...item, cumulativeCost, dynamicEstimation: currentCost });
    return acc;
  }, []);

  const totalEstimation = items.reduce((acc, item) => acc + calculateEstimation(item), 0);
  const totalDays = items.reduce((acc, item) => acc + Number(item.days_required || 0), 0);

  return (
    <div className="space-y-6">
      <TopMenu
        filters={filters}
        setFilters={setFilters}
        project={project}
        items={items}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        calculateEstimation={calculateEstimation}
        onMultipleDelete={() => {
          setItems(items.filter((i) => !selectedItems.some((s) => s._id === i._id)));
          setSelectedItems([]);
        }}
        onTicketCreated={(newTicket) => {
          setItems([...items, newTicket]);
        }}
      />

      <div className="bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <div className="no-scrollbar h-[calc(100vh-130px)] overflow-y-auto overflow-x-auto">
          <table className="w-full table-fixed min-w-max">
            <thead className="bg-slate-50/40 border-b border-slate-200/60">
              <tr>
                <th className="px-1 py-2 w-8 border-r border-slate-200/60">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(items);
                        } else {
                          setSelectedItems([]);
                        }
                      }}
                    />
                  </div>
                </th>
                <SortableHeader title="#" sortKey="index" sortBy={sortBy} setSortBy={setSortBy} className="w-12 text-center border-r border-slate-200/60" />
                <SortableHeader title="Titre" sortKey="title" sortBy={sortBy} setSortBy={setSortBy} className="w-1/3 text-center border-r border-slate-200/60" />
                <SortableHeader title="Module" sortKey="module_name" sortBy={sortBy} setSortBy={setSortBy} className="w-48 text-center border-r border-slate-200/60" />
                <SortableHeader title="Urgent" sortKey="urgent" sortBy={sortBy} setSortBy={setSortBy} className="w-20 text-center border-r border-slate-200/60" />
                <SortableHeader title="Status" sortKey="status" sortBy={sortBy} setSortBy={setSortBy} className="w-32 text-center border-r border-slate-200/60" />
                <th className="px-2 py-2 text-center w-32 border-r border-slate-200/60">
                  <h3 className="text-xs font-medium text-slate-600 uppercase tracking-wider">Tags</h3>
                </th>
                <SortableHeader title="Budget" sortKey="budget_name" sortBy={sortBy} setSortBy={setSortBy} className="w-52 text-center border-r border-slate-200/60" />
                <SortableHeader title="Days Required" sortKey="days_required" sortBy={sortBy} setSortBy={setSortBy} className="w-28 text-center border-r border-slate-200/60" />
                <SortableHeader title="Estimation" sortKey="estimation" sortBy={sortBy} setSortBy={setSortBy} className="w-28 text-center border-r border-slate-200/60" />
                <th className="px-2 py-2 text-center w-28">
                  <h3 className="text-xs font-medium text-slate-600 uppercase tracking-wider">Coût cumulé</h3>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={10}>
                    <div className="py-12 flex w-full justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-blue-500"></div>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-slate-500 font-light">
                    Aucune donnée trouvée
                  </td>
                </tr>
              ) : (
                <AnimatePresence>
                  {itemsWithCumulativeCost.map((item, index) => {
                    const selected = selectedItems.some((i) => i._id === item._id);

                    return (
                      <motion.tr
                        key={item._id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className={`group hover:bg-gray-50 transition-colors duration-150 ${
                          selected ? "bg-blue-50 hover:bg-blue-100" : index % 2 === 0 ? "bg-gray-100" : "bg-gray-50"
                        }`}>
                        <td className="px-1 py-1 border-r border-slate-200/60">
                          <div
                            className="flex items-center justify-center cursor-pointer"
                            onClick={(e) => {
                              if (selected) {
                                setSelectedItems(selectedItems.filter((i) => i._id !== item._id));
                              } else {
                                setSelectedItems([...selectedItems, item]);
                              }
                            }}>
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition-all"
                              checked={selected}
                              onChange={() => {}}
                            />
                          </div>
                        </td>
                        <td className="px-2 py-2 cursor-pointer hover:bg-gray-200 transition-colors border-r border-slate-200/60">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex gap-2.5">
                              <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">#{item.index}</span>
                            </div>
                          </div>
                        </td>
                        <td
                          onClick={() => {
                            query.set("item_modal_id", item._id);
                            navigate({ search: query.toString() });
                          }}
                          className="px-2 py-2 cursor-pointer hover:bg-gray-200 transition-colors border-r border-slate-200/60">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex gap-2.5">
                              <span className="text-sm text-slate-800 font-medium line-clamp-2 leading-relaxed">{item.title}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center border-r border-slate-200/60">
                          <span className="text-sm text-slate-700 block truncate max-w-44" title={item.module_name}>
                            {item.module_name}
                          </span>
                        </td>
                        <EditableSelectCell
                          itemId={item._id}
                          field="urgent"
                          value={item.urgent ? "yes" : "no"}
                          options={["yes", "no"]}
                          onSave={handleSave}
                          className="border-r border-slate-200/60">
                          <span className="text-sm text-slate-700">{item.urgent ? "Yes" : "No"}</span>
                        </EditableSelectCell>
                        <EditableSelectCell
                          itemId={item._id}
                          field="status"
                          value={item.status || ""}
                          options={["TODO", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO", "DONE"]}
                          onSave={handleSave}
                          className="border-r border-slate-200/60">
                          <span className="text-sm text-slate-700">{item.status}</span>
                        </EditableSelectCell>
                        <td className="px-2 py-2 text-center border-r border-slate-200/60">
                          <div className="flex items-center justify-center gap-1.5">
                            {(item.roadlog_tags || []).slice(0, 2).map((tag) => (
                              <span key={tag._id} className="text-sm text-slate-700">
                                {tag.name}
                              </span>
                            ))}
                            {(item.roadlog_tags || []).length > 2 && <span className="text-sm text-slate-500">+{(item.roadlog_tags || []).length - 2}</span>}
                          </div>
                        </td>
                        <EditableBudgetSelectCell item={item} budgets={budgets} onSave={handleSave} className="border-r border-slate-200/60" />
                        <td className="px-2 py-2 text-center border-r border-slate-200/60">
                          <span className="text-sm text-slate-700 whitespace-nowrap">{item.days_required || 0}</span>
                        </td>
                        <td className="px-2 py-2 text-center border-r border-slate-200/60">
                          <span className="text-sm text-slate-700 whitespace-nowrap">{item.dynamicEstimation.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                            {item.cumulativeCost.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
                          </span>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              )}
              {/* Total row */}
              <tr className="bg-sky-700 text-white font-bold">
                <td className="px-2 py-1.5 text-sm font-semibold text-center border-r border-slate-200/60">Total</td>
                <td className="px-2 py-1.5 border-r border-slate-200/60"></td>
                <td className="px-2 py-1.5 border-r border-slate-200/60"></td>
                <td className="px-2 py-1.5 border-r border-slate-200/60"></td>
                <td className="px-2 py-1.5 border-r border-slate-200/60"></td>
                <td className="px-2 py-1.5 border-r border-slate-200/60"></td>
                <td className="px-2 py-1.5 border-r border-slate-200/60"></td>
                <td className="px-2 py-1.5 text-sm font-mono font-bold whitespace-nowrap tabular-nums text-center border-r border-slate-200/60">{totalDays.toFixed(1)}</td>
                <td className="px-2 py-1.5 text-sm font-mono font-bold whitespace-nowrap tabular-nums text-center border-r border-slate-200/60">
                  {totalEstimation.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
                </td>
                <td className="px-2 py-1.5 text-sm font-mono font-bold whitespace-nowrap tabular-nums text-center">
                  {totalEstimation.toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {itemModalId && (
        <ItemModal
          project={project}
          onClose={() => {
            query.delete("item_modal_id");
            navigate({ search: query.toString() });
          }}
          onUpdate={(item) => setItems(items.map((i) => (i._id === item._id ? item : i)))}
          onDelete={(item) => setItems(items.filter((i) => i._id !== item._id))}
        />
      )}
    </div>
  );
};

const SortableHeader = ({ title, sortKey, sortBy, setSortBy, className }) => (
  <th className={`px-2 py-2 ${className}`}>
    <button
      className="flex items-center justify-center gap-2 text-xs font-medium text-slate-600 uppercase tracking-wider hover:text-slate-800 transition-colors group w-full"
      onClick={() => {
        setSortBy(sortBy.key === sortKey ? { key: sortKey, value: sortBy.value * -1 } : { key: sortKey, value: 1 });
      }}>
      {title}
      <div className="flex flex-col h-3 justify-center opacity-40 group-hover:opacity-70 transition-opacity">
        {sortBy.key === sortKey && sortBy.value === 1 ? (
          <IoIosArrowUp className="text-blue-600 opacity-100" size={12} />
        ) : sortBy.key === sortKey && sortBy.value === -1 ? (
          <IoIosArrowDown className="text-blue-600 opacity-100" size={12} />
        ) : (
          <>
            <IoIosArrowUp size={10} />
            <IoIosArrowDown size={10} />
          </>
        )}
      </div>
    </button>
  </th>
);

const EditableSelectCell = ({ itemId, field, value, options, onSave, children, className = "" }) => {
  const [editing, setEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const cellRef = useRef(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleSave = async (e) => {
    const newValue = e.target.value;
    setCurrentValue(newValue);
    setEditing(false);
    await onSave(itemId, field, field === "urgent" ? newValue === "yes" : newValue);
  };

  const handleClickOutside = (event) => {
    if (cellRef.current && !cellRef.current.contains(event.target)) {
      setEditing(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <td ref={cellRef} onClick={() => setEditing(true)} className={`px-2 py-2 cursor-pointer hover:bg-gray-200 transition-colors text-center ${className}`}>
      {editing ? (
        <select
          className="text-sm border-0 bg-white rounded-lg shadow-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500/30 px-3 py-1.5 font-medium text-slate-700 min-w-[100px]"
          value={currentValue}
          onChange={handleSave}
          autoFocus>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <div className="text-sm font-medium text-slate-700">{children || currentValue}</div>
      )}
    </td>
  );
};

const EditableBudgetSelectCell = ({ item, budgets, onSave, className = "" }) => {
  const [editing, setEditing] = useState(false);
  const cellRef = useRef(null);

  const handleSave = async (e) => {
    setEditing(false);
    const budget = budgets.find((b) => b._id === e.target.value);
    if (!budget) {
      await onSave(item._id, { budget_id: "", budget_name: "" });
    } else {
      await onSave(item._id, { budget_id: budget._id, budget_name: budget.name });
    }
  };

  const handleClickOutside = (event) => {
    if (cellRef.current && !cellRef.current.contains(event.target)) {
      setEditing(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <td ref={cellRef} onClick={() => setEditing(true)} className={`px-2 py-2 cursor-pointer hover:bg-gray-200 transition-colors w-52 max-w-52 text-center ${className}`}>
      {editing ? (
        <select
          className="text-sm border-0 bg-white rounded-lg shadow-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500/30 px-3 py-1.5 font-medium text-slate-700 w-full"
          value={item.budget_id || ""}
          onChange={handleSave}
          autoFocus>
          <option value="">Aucun</option>
          {budgets.map((budget) => (
            <option key={budget._id} value={budget._id}>
              {budget.name}
            </option>
          ))}
        </select>
      ) : (
        <div className="text-sm font-medium text-slate-700 overflow-x-auto whitespace-nowrap max-w-48" title={item.budget_name}>
          {item.budget_name || <span className="text-slate-400 italic">Aucun</span>}
        </div>
      )}
    </td>
  );
};
